<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

// DV: compact client dossier for the «Данные клиента» note (account 1).
// Data comes from content_attributes.dv_client_dossier (snake or camel case).
const props = defineProps({
  dossier: { type: Object, required: true },
});

const { t, locale } = useI18n();
const showHistory = ref(false);

const camel = key => key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
const pick = (object, key) => {
  if (!object || typeof object !== 'object') return undefined;
  return object[key] !== undefined ? object[key] : object[camel(key)];
};
const list = (object, key) => {
  const value = pick(object, key);
  return Array.isArray(value) ? value : [];
};

const today = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};
const parseDate = value => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? new Date(+match[1], +match[2] - 1, +match[3]) : null;
};
const formatDate = (value, options) => {
  const date = parseDate(value);
  return date
    ? new Intl.DateTimeFormat(locale.value || 'ru', options).format(date)
    : '—';
};
const money = amount => {
  const rounded = Math.round(Number(amount || 0) * 100) / 100;
  return `${new Intl.NumberFormat('ru-RU').format(rounded)} $`;
};
const label = (group, value) => {
  const normalized = String(value || '').toLowerCase();
  if (!normalized) return '';
  const key = `CONTACT_PANEL.ACTION_CENTER.${group}.${normalized}`;
  const translated = t(key);
  return translated === key ? normalized : translated;
};
const T = (key, params) => t(`CONTACT_PANEL.DV_DOSSIER.${key}`, params || {});

const statusTone = status => {
  const value = String(status || '').toLowerCase();
  if (['confirmed', 'time_sent', 'paid', 'completed'].includes(value))
    return 'bg-n-teal-3 text-n-teal-11';
  if (value === 'deposit') return 'bg-n-blue-3 text-n-blue-11';
  if (['cancelled', 'canceled'].includes(value)) return 'bg-n-ruby-3 text-n-ruby-11';
  return 'bg-n-amber-3 text-n-amber-11';
};

const kind = computed(() => {
  const value = pick(props.dossier, 'kind');
  return {
    repeat: T('KIND_REPEAT'),
    client: T('KIND_CLIENT'),
    lead: T('KIND_LEAD'),
  }[value] || T('KIND_LEAD');
});

const summaryLine = computed(() => {
  const orders = Number(pick(props.dossier, 'orders_count') || 0);
  const parts = [T('ORDERS', { count: orders })];
  const paid = Number(pick(props.dossier, 'paid_usd') || 0);
  if (paid > 0) parts.push(T('PAID', { amount: money(paid) }));
  const firstAt = pick(props.dossier, 'first_at');
  if (firstAt) {
    parts.push(
      T('SINCE', {
        date: formatDate(firstAt, { month: '2-digit', year: 'numeric' }),
      })
    );
  }
  const cancelled = Number(pick(props.dossier, 'cancelled_count') || 0);
  if (cancelled > 0) parts.push(T('CANCELLED', { count: cancelled }));
  return parts.join(' · ');
});

const relative = value => {
  const date = parseDate(value);
  if (!date) return '';
  const days = Math.round((date - today()) / 86400000);
  if (days === 0) return T('TODAY');
  if (days === 1) return T('TOMORROW');
  if (days > 1) return T('IN_DAYS', { count: days });
  return T('DAYS_AGO', { count: -days });
};

const guestsLabel = guests => {
  if (!guests) return '';
  const parts = [];
  const adult = Number(pick(guests, 'adult') || 0);
  const child = Number(pick(guests, 'child') || 0);
  const infant = Number(pick(guests, 'infant') || 0);
  if (adult) parts.push(t('CONTACT_PANEL.ACTION_CENTER.DV_BOOKINGS.ADULTS', { count: adult }));
  if (child) parts.push(t('CONTACT_PANEL.ACTION_CENTER.DV_BOOKINGS.CHILDREN', { count: child }));
  if (infant) parts.push(t('CONTACT_PANEL.ACTION_CENTER.DV_BOOKINGS.INFANTS', { count: infant }));
  return parts.join(' + ');
};

const activeBookings = computed(() =>
  list(props.dossier, 'active').map(booking => {
    const due = Number(pick(booking, 'amount_due') || 0);
    const total = Number(pick(booking, 'total_price') || 0);
    const paid = Number(pick(booking, 'paid_amount') || 0);
    const pickup = pick(booking, 'pickup_time');
    const draft = pick(booking, 'pickup_time_draft');
    const date = pick(booking, 'start_date');
    let payment = T('PAYMENT_PAID', { total: money(total) });
    if (due > 0) {
      payment = paid > 0
        ? T('PAYMENT_DUE', { paid: money(paid), total: money(total), due: money(due) })
        : T('PAYMENT_UNPAID', { due: money(due) });
    }
    return {
      id: pick(booking, 'booking_id'),
      tour: pick(booking, 'tour') || '—',
      dateLabel: formatDate(date, { weekday: 'short', day: '2-digit', month: '2-digit' }),
      relative: relative(date),
      status: pick(booking, 'status'),
      statusLabel: label('BOOKING_STATUS', pick(booking, 'status')),
      pickup: pickup || (draft ? T('PICKUP_DRAFT', { time: draft }) : T('PICKUP_NONE')),
      pickupMissing: !pickup,
      guide: pick(booking, 'guide_name') || T('GUIDE_NONE'),
      guideMissing: !pick(booking, 'guide_name'),
      guests: guestsLabel(pick(booking, 'guests')),
      hotel: pick(booking, 'hotel_name'),
      payment,
      paymentDue: due > 0,
      portal: pick(booking, 'client_portal_url'),
    };
  })
);

// Booking cards already highlight missing pickup, guide and debt in amber;
// only urgent signals (tour today/tomorrow) get their own red line.
const signals = computed(() =>
  list(props.dossier, 'signals')
    .filter(signal => pick(signal, 'tone') === 'danger')
    .map((signal, index) => ({
      key: index,
      text: pick(signal, 'text'),
      danger: true,
    }))
);

const history = computed(() =>
  list(props.dossier, 'history').map(booking => ({
    id: pick(booking, 'booking_id'),
    tour: pick(booking, 'tour') || '—',
    date: formatDate(pick(booking, 'start_date'), {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    }),
    status: pick(booking, 'status'),
    statusLabel: label('BOOKING_STATUS', pick(booking, 'status')),
    total: money(pick(booking, 'total_price')),
  }))
);
const historyCount = computed(() => Number(pick(props.dossier, 'history_count') || 0));
const adminUrl = computed(() => pick(props.dossier, 'admin_url'));
const name = computed(() => pick(props.dossier, 'name') || T('NO_NAME'));
</script>

<template>
  <div class="flex min-w-0 flex-col gap-2.5 text-n-slate-12">
    <div class="flex items-start justify-between gap-2">
      <div class="min-w-0">
        <p class="mb-0 flex items-center gap-1.5 text-sm font-semibold text-n-amber-12">
          <span class="i-lucide-user-round-search size-4 shrink-0 text-n-amber-11" />
          <span class="truncate">{{ name }}</span>
        </p>
        <p class="mb-0 mt-0.5 text-xs text-n-amber-11">{{ summaryLine }}</p>
      </div>
      <span class="shrink-0 rounded-md bg-n-amber-4 px-1.5 py-0.5 text-[11px] font-medium leading-4 text-n-amber-11">
        {{ kind }}
      </span>
    </div>

    <ul v-if="signals.length" class="m-0 flex list-none flex-col gap-1 p-0">
      <li
        v-for="signal in signals"
        :key="signal.key"
        class="flex items-start gap-1.5 rounded-md px-2 py-1 text-xs leading-4"
        :class="signal.danger ? 'bg-n-ruby-3 text-n-ruby-11' : 'bg-n-amber-3 text-n-amber-11'"
      >
        <span class="i-lucide-triangle-alert mt-px shrink-0 text-xs" />
        <span>{{ signal.text }}</span>
      </li>
    </ul>

    <p v-if="!activeBookings.length" class="mb-0 rounded-lg bg-n-solid-1 px-3 py-2 text-xs text-n-slate-11">
      {{ T('NO_ACTIVE') }}
    </p>

    <div
      v-for="booking in activeBookings"
      :key="booking.id"
      class="rounded-lg bg-n-solid-1 px-3 py-2.5"
    >
      <div class="flex items-start justify-between gap-2">
        <div class="min-w-0">
          <p class="mb-0 truncate text-sm font-semibold leading-5">{{ booking.tour }}</p>
          <p class="mb-0 text-xs leading-4 text-n-slate-11">
            #{{ booking.id }} · {{ booking.dateLabel }}
            <span v-if="booking.relative" class="font-medium text-n-slate-12">· {{ booking.relative }}</span>
          </p>
        </div>
        <span
          class="shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-medium leading-4"
          :class="statusTone(booking.status)"
        >
          {{ booking.statusLabel }}
        </span>
      </div>
      <dl class="mb-0 mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs leading-4">
        <dt class="text-n-slate-10">{{ T('PICKUP') }}</dt>
        <dd class="mb-0 text-right" :class="booking.pickupMissing ? 'text-n-amber-11' : ''">{{ booking.pickup }}</dd>
        <dt class="text-n-slate-10">{{ T('GUIDE') }}</dt>
        <dd class="mb-0 truncate text-right" :class="booking.guideMissing ? 'text-n-amber-11' : ''">{{ booking.guide }}</dd>
        <template v-if="booking.guests">
          <dt class="text-n-slate-10">{{ T('GUESTS') }}</dt>
          <dd class="mb-0 text-right">{{ booking.guests }}</dd>
        </template>
        <template v-if="booking.hotel">
          <dt class="text-n-slate-10">{{ T('HOTEL') }}</dt>
          <dd class="mb-0 truncate text-right" :title="booking.hotel">{{ booking.hotel }}</dd>
        </template>
        <dt class="text-n-slate-10">{{ T('PAYMENT') }}</dt>
        <dd class="mb-0 text-right font-medium" :class="booking.paymentDue ? 'text-n-amber-11' : 'text-n-teal-11'">{{ booking.payment }}</dd>
      </dl>
      <a
        v-if="booking.portal"
        :href="booking.portal"
        target="_blank"
        rel="noopener noreferrer"
        class="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-n-brand hover:underline"
      >
        <span class="i-lucide-key-round text-xs" />{{ T('PORTAL') }}
      </a>
    </div>


    <div v-if="historyCount" class="border-t border-n-amber-6 pt-2">
      <button
        type="button"
        class="inline-flex items-center gap-1 text-xs font-medium text-n-amber-11 hover:underline"
        @click="showHistory = !showHistory"
      >
        <span
          class="i-lucide-chevron-right size-3.5 transition-transform"
          :class="{ 'rotate-90': showHistory }"
        />
        {{ T('HISTORY', { count: historyCount }) }}
      </button>
      <div v-if="showHistory" class="mt-1.5 flex flex-col gap-1">
        <div
          v-for="item in history"
          :key="item.id"
          class="flex items-center gap-2 rounded-md bg-n-solid-1 px-2 py-1 text-xs"
        >
          <span class="shrink-0 tabular-nums text-n-slate-10">{{ item.date }}</span>
          <span class="min-w-0 flex-1 truncate">{{ item.tour }}</span>
          <span class="shrink-0 rounded px-1 text-[10px] leading-4" :class="statusTone(item.status)">{{ item.statusLabel }}</span>
          <span class="shrink-0 tabular-nums text-n-slate-11">{{ item.total }}</span>
        </div>
        <p v-if="historyCount > history.length" class="mb-0 text-[11px] text-n-amber-11">
          {{ T('HISTORY_MORE', { count: historyCount - history.length }) }}
        </p>
      </div>
    </div>

    <a
      v-if="adminUrl"
      :href="adminUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="inline-flex w-fit items-center gap-1 text-xs font-medium text-n-amber-11 hover:underline"
    >
      {{ T('OPEN_ADMIN') }}
      <span class="i-lucide-arrow-up-right text-xs" />
    </a>
  </div>
</template>
